<template>
    <div class="ui-page">
        <ui-page-header title="Tipos de documentos"
            subtitle="Catálogo único da fiscalização mensal. Cada caso escolhe quais tipos pede à recuperanda; o template da BeJ padroniza o envio.">
            <v-btn outlined :disabled="!items.length" @click="exportCatalog"><v-icon left small>mdi-microsoft-excel</v-icon>Exportar (.xlsx)</v-btn>
            <v-btn v-if="$allowed('rma.document_types')" color="primary" depressed @click="openEdit(null)"><v-icon left small>mdi-plus</v-icon>Novo tipo</v-btn>
        </ui-page-header>

        <ui-table-card>
            <v-data-table :headers="headers" :items="items" :loading="loading" disable-sort disable-pagination hide-default-footer
                no-data-text="Nenhum tipo cadastrado" loading-text="Carregando..." class="ui-clickable-rows" @click:row="openEdit">
                <template v-slot:[`item.sort_order`]="{ item }"><span class="ui-muted">{{ String(item.sort_order).padStart(2, "0") }}</span></template>
                <template v-slot:[`item.name`]="{ item }">
                    <div class="ui-cell-main">{{ item.name }}</div>
                    <div v-if="item.instructions" class="ui-cell-sub">{{ item.instructions }}</div>
                </template>
                <template v-slot:[`item.accepted_extensions`]="{ item }"><span class="ui-mono">{{ formatExtensions(item.accepted_extensions) }}</span></template>
                <template v-slot:[`item.template`]="{ item }">
                    <v-btn v-if="item.has_template" small text color="primary" @click.stop="downloadTemplate(item)">
                        <v-icon left small>mdi-download</v-icon>{{ item.template_file_name || "Baixar" }}
                    </v-btn>
                    <span v-else class="ui-muted">—</span>
                </template>
                <template v-slot:[`item.active`]="{ item }"><ui-badge :status="item.active ? 'active' : 'inactive'" /></template>
                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        { text: 'Editar', icon: 'mdi-pencil-outline', handler: () => openEdit(item) , disabled: !$allowed('rma.document_types') },
                        { text: 'Baixar template', icon: 'mdi-download', handler: () => downloadTemplate(item), hidden: !item.has_template },
                        { text: 'Excluir', icon: 'mdi-delete-outline', handler: () => confirmRemove(item), danger: true , disabled: !$allowed('rma.document_types') },
                    ]" />
                </template>
            </v-data-table>
        </ui-table-card>

        <v-dialog v-model="edit.open" max-width="620" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">{{ edit.id ? "Editar tipo de documento" : "Novo tipo de documento" }}</h3>
                        <p class="ui-section-description">O nome e a orientação aparecem para a recuperanda no checklist do mês.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-text-field v-model="edit.form.name" label="Nome" outlined dense hide-details="auto" class="mb-4" maxlength="255"></v-text-field>
                    <v-textarea v-model="edit.form.instructions" label="Orientação à recuperanda (opcional)" outlined dense rows="2" auto-grow hide-details="auto"
                        placeholder="Ex.: Envie um PDF por conta, sem senha, com o saldo do último dia do mês." class="mb-4"></v-textarea>
                    <v-select v-model="edit.form.accepted_extensions" :items="extensionItems" label="Formatos aceitos" multiple chips small-chips deletable-chips
                        outlined dense hide-details="auto" class="mb-4" :menu-props="{ offsetY: true }"></v-select>
                    <v-row dense>
                        <v-col cols="6"><v-text-field v-model="edit.form.sort_order" type="number" min="0" label="Ordem no checklist" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="6" class="d-flex align-center"><v-switch v-model="edit.form.active" inset dense hide-details label="Ativo"></v-switch></v-col>
                    </v-row>

                    <div class="ui-divider"></div>
                    <div class="ui-label">Template da BeJ (opcional)</div>
                    <p class="ui-small ui-muted mb-3">A recuperanda baixa o template no item e devolve preenchido.</p>
                    <div v-if="edit.id && edit.current && !edit.file" class="d-flex align-center flex-wrap mb-3" style="gap: 8px;">
                        <v-icon small>mdi-file-excel-outline</v-icon>
                        <span class="ui-cell-main">{{ edit.current.template_file_name || "Template atual" }}</span>
                        <v-spacer></v-spacer>
                        <v-btn small outlined @click="downloadTemplate(edit.current)"><v-icon left small>mdi-download</v-icon>Baixar</v-btn>
                        <v-btn v-if="$allowed('rma.document_types')" small outlined class="ui-btn-danger" :loading="edit.removingTemplate" @click="removeTemplate">Remover</v-btn>
                    </div>
                    <ui-file-drop save-action="rma.document_types" :file="edit.file" accept=".xlsx,.xls,.pdf,.docx,.csv" :max-size-mb="50" hint="XLSX, XLS, CSV, PDF ou DOCX de até 50 MB"
                        :loading="edit.saving" @select="edit.file = $event" @save="save" @error="(message) => $dialog.notify.warning(message, { position: 'top-right', timeout: 4000 })" />
                    <div v-if="edit.uploading" class="mt-3" role="status" aria-live="polite">
                        <div class="d-flex align-center ui-small mb-1">
                            <span>{{ edit.progress >= 100 ? "Concluindo envio do template" : "Enviando template" }}</span>
                            <v-spacer></v-spacer>
                            <span style="font-weight: 600; font-variant-numeric: tabular-nums;">{{ edit.progress }}%</span>
                        </div>
                        <v-progress-linear :value="edit.progress" :indeterminate="edit.progress >= 100" height="6" rounded color="primary"
                            background-color="#e2e8f0" aria-label="Progresso do envio do template"></v-progress-linear>
                    </div>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="edit.saving" @click="edit.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('rma.document_types')" color="primary" depressed :loading="edit.saving" :disabled="!canSave" @click="save">Salvar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import { downloadXlsx } from "@/common/util/export-xlsx.util";
import { formatExtensions, notifyError, notifySuccess, openUrl, uploadFile } from "@/components/rma/rma.util";

const EXTENSIONS = ["pdf", "xlsx", "xls", "csv", "txt", "png", "jpg", "jpeg", "zip"];

export default {
    name: "RmaDocumentTypeList",

    data: () => ({
        items: [],
        loading: false,
        headers: [
            { text: "#", value: "sort_order", width: 48 },
            { text: "Tipo", value: "name" },
            { text: "Formatos", value: "accepted_extensions", width: 180 },
            { text: "Template", value: "template", width: 220 },
            { text: "Situação", value: "active", width: 110 },
            { text: "", value: "actions", align: "end", width: 48 },
        ],
        extensionItems: EXTENSIONS.map((value) => ({ value, text: value.toUpperCase() })),
        edit: { open: false, id: null, current: null, file: null, saving: false, uploading: false, progress: 0, removingTemplate: false, form: {} },
    }),

    computed: {
        canSave() {
            return !!String(this.edit.form.name || "").trim() && (this.edit.form.accepted_extensions || []).length > 0;
        },
    },

    created() {
        this.load();
    },

    methods: {
        formatExtensions,

        async load() {
            this.loading = true;
            try {
                const result = await axios.get("/api/admin/rma/document-types");
                this.items = result.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },

        openEdit(item) {
            this.edit = {
                open: true,
                id: item ? item.id : null,
                current: item && item.has_template ? item : null,
                file: null,
                saving: false,
                uploading: false,
                progress: 0,
                removingTemplate: false,
                form: item
                    ? { name: item.name, instructions: item.instructions || "", accepted_extensions: [...item.accepted_extensions], sort_order: item.sort_order, active: item.active }
                    : { name: "", instructions: "", accepted_extensions: ["pdf"], sort_order: "", active: true },
            };
        },

        async save() {
            if (!this.canSave) return;
            this.edit.saving = true;
            try {
                const payload = { ...this.edit.form, sort_order: this.edit.form.sort_order === "" ? undefined : Number(this.edit.form.sort_order) };
                const result = this.edit.id
                    ? await axios.patch(`/api/admin/rma/document-types/${this.edit.id}`, payload)
                    : await axios.post("/api/admin/rma/document-types", payload);
                const type = result.data.content;
                if (this.edit.file) {
                    // A API recebe o template e repassa ao storage.
                    this.edit.progress = 0;
                    this.edit.uploading = true;
                    await uploadFile(`/api/admin/rma/document-types/${type.id}/template`, this.edit.file, {
                        method: "put",
                        onProgress: ({ percent }) => {
                            this.edit.progress = percent;
                        },
                    });
                }
                notifySuccess(this, this.edit.id ? "Tipo de documento atualizado." : "Tipo de documento criado.");
                this.edit.open = false;
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.edit.saving = false;
                this.edit.uploading = false;
            }
        },

        async removeTemplate() {
            this.edit.removingTemplate = true;
            try {
                await axios.delete(`/api/admin/rma/document-types/${this.edit.id}/template`);
                this.edit.current = null;
                notifySuccess(this, "Template removido.");
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.edit.removingTemplate = false;
            }
        },

        async downloadTemplate(item) {
            try {
                const result = await axios.get(`/api/admin/rma/document-types/${item.id}/template`);
                openUrl(result.data.content.url);
            } catch (error) {
                notifyError(this, error);
            }
        },

        async confirmRemove(item) {
            const confirmed = await this.$dialog.confirm({
                title: "Excluir tipo de documento",
                text: `"${item.name}" deixa de aparecer nos checklists dos próximos meses. As competências já abertas mantêm o item e os arquivos enviados. Confirma?`,
                actions: { false: "Cancelar", true: { text: "Excluir", color: "error" } },
            });
            if (!confirmed) return;
            try {
                await axios.delete(`/api/admin/rma/document-types/${item.id}`);
                notifySuccess(this, "Tipo de documento excluído.");
                await this.load();
            } catch (error) {
                notifyError(this, error);
            }
        },

        exportCatalog() {
            downloadXlsx(this.items.map((item) => ({
                Ordem: item.sort_order,
                Tipo: item.name,
                Orientação: item.instructions || "",
                Formatos: formatExtensions(item.accepted_extensions),
                Template: item.has_template ? item.template_file_name || "Sim" : "",
                Situação: item.active ? "Ativo" : "Inativo",
            })), "Catalogo_tipos_documentos_RMA");
        },
    },
};
</script>
